// src/path/to/CreateEmergencyPurchaseOrderParts.tsx
import React, { useEffect, useRef, useState } from "react";
import SelectBox from "../../../../components/elements/form/selectBox";
import { useSpearPartsApi } from "../../../../store/api/hooks/spearPartsApiHook";
import { useCommonApi } from "../../../../store/api/hooks/commonApiHook";
import Currency from "../../../../components/currency";

interface CreateEmergencyPurchaseOrderPartsProps {
    onPartsChange?: (parts: any[]) => void;
    initialParts?: any[]; // optional seed from parent (in child-internal shape)
    savedtax?: any;
    onSetTax?: (tax: any) => void;
}

const CreateEmergencyPurchaseOrderParts: React.FC<CreateEmergencyPurchaseOrderPartsProps> = ({ onPartsChange, initialParts = [], savedtax = null, onSetTax }) => {
    const divRef = useRef<any>(null);
    const sparePartsAPI = useSpearPartsApi();
    const commonApi: any = useCommonApi();

    const blankpartmodel = {
        partsID: null,
        vendorPartCode: ``,
        vendorPartName: ``,
        vendorID: null,
        vendorName: ``,
        requiredQty: 0,
        unitID: null,
        unitPrice: 0,
        unitName: ``,
        totalAmount: 0,
        isEdit: false,
        // optional fields used elsewhere
        woSparePartMappingID: null,
        partCode: ``,
        partName: ``,
        pickedQty: 0,
        usedQty: 0,
        facilityID: null,
        subFacilityID: null,
        isPicked: false,
        isLocked: false,
        rate: undefined,
    };

    const [partsItems, setPartsItems] = useState<any[]>(initialParts.length ? initialParts : []);
    const [lastSearchParts, setLastSearchParts] = useState<any[]>([]);
    const [vendorList, setVendorList] = useState<any[]>([]);
    const [taxes, setTaxes] = useState<any[]>([]);
    const [tax, setTax] = useState<any>(null);

    const getTaxes = async () => {
        return new Promise((resolve, reject) => {
            commonApi.getTaxes({}, (resp: any) => {
                const taxesList = resp.taxRates.map((tax: any) => ({
                    label: tax.taxRateName,
                    value: tax.taxRateID,
                    rate: tax.taxRate,
                }));
                setTaxes(taxesList);
                if (!savedtax)
                    setTax(taxesList[0] || null);
                resolve(resp.taxRates);
            }, (error: any) => {
                reject([]);
            });
        });
    };

    // Format parts into the exact shape parent/save API expects
    const formatForParent = (items: any[]) => {
        return items.map((p: any) => {
            const formatted: any = {
                woSparePartMappingID: p.woSparePartMappingID ?? null,
                sparePartID: p.partsID ?? p.sparePartID ?? null,
                assetID: p.assetID ?? null,
                requiredQty: p.requiredQty ?? 0,
                pickedQty: p.pickedQty ?? 0,
                usedQty: p.usedQty ?? 0,
                facilityID: p.facilityID ?? null,
                subFacilityID: p.subFacilityID ?? p.subFaclityID ?? null,
                isPicked: p.isPicked ?? false,
                isLocked: p.isLocked ?? false,
                partCode: p.vendorPartCode ?? p.partCode ?? "",
                partName: p.vendorPartName ?? p.partName ?? "",
            };
            // include rate only if present
            if (p.unitPrice !== undefined && p.unitPrice !== null && p.unitPrice !== '') {
                formatted.rate = p.unitPrice;
            } else if (p.rate !== undefined && p.rate !== null && p.rate !== '') {
                formatted.rate = p.rate;
            }
            return formatted;
        });
    };

    // notify parent on parts change
    useEffect(() => {
        if (onPartsChange) {
            onPartsChange(formatForParent(partsItems));
        }
    }, [partsItems]);

    const onOutsideClick = () => {
        handleOnClickCellEdit();
    };

    const handleOnClickCellEdit = (identifier: string = ``, e: any = null) => {
        if (identifier) {
            const cellElement = document.querySelector(`#${identifier}`) as HTMLDivElement;
            const previewElement = cellElement?.querySelector(`.preview-value`) as HTMLDivElement;
            const editElement = cellElement?.querySelector(`.edit-value`) as HTMLDivElement;
            if (previewElement && editElement) {
                if (cellElement.id === `${identifier}`) {
                    if (!previewElement.classList.contains(`hidden`)) previewElement.classList.add(`hidden`);
                    if (editElement.classList.contains(`hidden`)) editElement.classList.remove(`hidden`);
                }
            }
        }

        const editableList = document.querySelectorAll(`.editable`) as NodeListOf<HTMLDivElement>;
        for (let i = 0; i < editableList.length; i++) {
            const editableCell = editableList[i];
            const preview = editableCell.querySelector(`.preview-value`) as HTMLDivElement;
            const edit = editableCell.querySelector(`.edit-value`) as HTMLDivElement;
            if (editableCell.id !== `${identifier}`) {
                if (preview?.classList.contains(`hidden`)) preview?.classList?.remove(`hidden`);
                if (!edit?.classList.contains(`hidden`)) edit?.classList?.add(`hidden`);
            }
        }
    };

    const handlePartsSearch = async (inputval: any): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            sparePartsAPI.getSpearPartsList({
                pageSize: 10,
                pageNumber: 1,
                searchParam: inputval,
            }, (resp: any) => {
                if (resp && resp.code === 200 && resp.successful === true && resp.hasOwnProperty(`sparePartsList`) && Array.isArray(resp.sparePartsList) && resp.sparePartsList.length > 0) {
                    const bindParts = resp.sparePartsList.map((part: any) => {
                        return {
                            value: part.partsCode,
                            label: part.sparePartsName,
                        };
                    });
                    setLastSearchParts(resp.sparePartsList);
                    resolve(bindParts);
                } else {
                    resolve([]);
                }
            }, (error: any) => {
                reject([]);
            });
        });
    };

    const handleVendorSearch = async (inputval: any): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            commonApi.GetServiceprovidertList({}, (resp: any) => {
                if (resp && resp.hasOwnProperty(`vendorList`) && Array.isArray(resp.vendorList) && resp.vendorList.length > 0) {
                    resolve(resp.vendorList);
                } else {
                    resolve([]);
                }
            }, (error: any) => {
                reject([]);
            });
        });
    };

    const init_page_data = async () => {
        const vendorlist = await handleVendorSearch(``);
        setVendorList(vendorlist);
    };

    useEffect(() => {
        getTaxes();
        init_page_data();
        setTax(savedtax || null);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (divRef.current && !divRef.current.contains(event.target)) {
                onOutsideClick();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: any) => {
            if (event.key === 'Escape') {
                onOutsideClick();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const getSubTotal = () => {
        return partsItems.reduce((total, item) => {
            const itemCost = parseFloat(item.unitPrice) || parseFloat(item.rate || 0) || 0;
            const itemQuantity = parseFloat(item.requiredQty) || 0;
            return total + itemCost * itemQuantity;
        }, 0).toFixed(2);
    };
    const getTotal = () => {
        const subTotal = Number(getSubTotal());
        const taxRate = subTotal * (tax?.rate || 0) / 100;
        return (subTotal + taxRate).toFixed(2);
    };

    useEffect(() => {
        if (onSetTax) {
            onSetTax(tax);
        }
    }, [tax, onSetTax]);

    const handleAddPart = () => {
        const newPart = Object.assign({}, JSON.parse(JSON.stringify(blankpartmodel)));
        setPartsItems(prev => [...prev, newPart]);
    };

    return (
        <div>
            <div className="flex justify-between items-center mt-1">
                <h4 className='font-semibold text-[0.938rem] py-4 uppercase'>ITEMS</h4>
                <button type={`button`} className='text-primary' onClick={handleAddPart}>Add parts</button>
            </div>

            <div className={`border border-[#043E6133] rounded-[5px] parts-table`}>
                <div className={`flex items-center flex-nowrap bg-[#F0F0F0] rounded-ss-[5px] rounded-se-[5px]`}>
                    <div className={`w-[155px] flex-shrink-0 font-semibold text-[#262A2D] text-[13px] px-2.5 py-2 h-[50px] flex items-center`}>item Code</div>
                    <div className={`w-[250px] flex-shrink-0 grow font-semibold text-[#262A2D] text-[13px] px-2.5 py-2 h-[50px] flex items-center`}>Item Name</div>
                    <div className={`w-[160px] flex-shrink-0 font-semibold text-[#262A2D] text-[13px] px-2.5 py-2 h-[50px] flex items-center justify-end`}>Cost </div>
                    <div className={`w-[120px] flex-shrink-0 font-semibold text-[#262A2D] text-[13px] px-2.5 py-2 h-[50px] flex items-center justify-end`}>Quantity</div>
                    <div className={`w-[120px] flex-shrink-0 font-semibold text-[#262A2D] text-[13px] px-2.5 py-2 h-[50px] flex items-center justify-end`}>Total</div>
                    <div className={`w-[32px] flex-shrink-0 font-semibold text-[#262A2D] text-[13px] px-0 py-2 h-[50px] flex items-center`}> </div>
                </div>
                <div className={``} ref={divRef}>
                    {partsItems.length === 0 && (
                        <div className={`flex items-center justify-center h-24 text-gray-500`}>
                            No parts added yet.
                        </div>
                    )}
                    {partsItems.map((part, index) => (
                        <div className={`flex items-center flex-nowrap ${index < (partsItems.length - 1) ? `border-b border-[#043E6133]` : ``}`} key={`part-item-${index}`}>
                            <div className={`w-[155px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 min-h-12 max-h-12 flex items-center bg-[#F9F9F9] cursor-not-allowed`}>{part.vendorPartCode || `--`}</div>

                            <div className={`w-[250px] flex-shrink-0 grow font-normal text-[#42484B] text-[15px] px-2.5 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} id={`part-item-name-${index}`} onClick={(e: any) => {
                                handleOnClickCellEdit(`part-item-name-${index}`, e);
                                e.stopPropagation();
                                e.preventDefault();
                            }}>
                                <div className={`preview-value line-clamp-1`} title={part.vendorName ? part.vendorName : `Click here to add data`}>{part.vendorPartName || `--`}</div>
                                <div className={`edit-value hidden`}>
                                    <SelectBox
                                        label={``}
                                        value={part ? { label: part.vendorPartName, value: part.vendorPartCode } : null}
                                        onChange={(value: any) => {
                                            const updatedParts = [...partsItems];
                                            updatedParts[index].vendorPartName = value?.label || ``;
                                            updatedParts[index].vendorPartCode = value?.value || ``;
                                            const cronologicalPart: any = lastSearchParts.find((p: any) => p.partsCode === value?.value);
                                            updatedParts[index].partsID = cronologicalPart?.partsID || null;
                                            updatedParts[index].unitID = cronologicalPart?.unitID || null;
                                            updatedParts[index].unitName = cronologicalPart?.unitCode || `--`;
                                            updatedParts[index].unitPrice = cronologicalPart?.pricePerUnit || cronologicalPart?.rate || updatedParts[index].unitPrice || 0;
                                            // also set partCode/partName for parent mapping convenience
                                            updatedParts[index].partCode = cronologicalPart?.partsCode || updatedParts[index].partCode || '';
                                            updatedParts[index].partName = cronologicalPart?.sparePartsName || updatedParts[index].partName || '';
                                            setPartsItems(updatedParts);
                                        }}
                                        required
                                        placeholder='Search & Select Part'
                                        options={[]}
                                        className={`!my-0`}
                                        customStyleOption={{ control: (base) => ({ ...base, marginTop: `0rem`, minHeight: '32px' }) }}
                                        async={true}
                                        isClearable={true}
                                        asyncLoadOptions={async (inputValue: string) => {
                                            return handlePartsSearch(inputValue);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className={`w-[160px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} id={`part-item-unit-price-${index}`} onClick={(e: any) => {
                                handleOnClickCellEdit(`part-item-unit-price-${index}`, e);
                                e.stopPropagation();
                                e.preventDefault();
                            }}>
                                <div className={`preview-value line-clamp-1 text-right`} title={`Click here to add/update data`}>
                                    <Currency currency={`USD`} value={part.unitPrice ? Number(part.unitPrice) : (part.rate ? Number(part.rate) : 0)} />
                                </div>
                                <div className={`edit-value hidden`}>
                                    <div className={`flex`}>
                                        <span className={`border border-gray-300 bg-gray-50 rounded-tl rounded-bl flex items-center justify-center px-2 py-1 border-r-0`}>$</span>
                                        <input
                                            type="number"
                                            name={`poinventoryunitprice${index}`}
                                            className={`w-full border border-gray-300 rounded-tr rounded-br px-2 py-1 outline-none text-right input-no-spinner`}
                                            value={part.unitPrice ?? part.rate ?? 0}
                                            min={0}
                                            onInput={(e: any) => {
                                                const newVal = e.target.value;
                                                const updatedParts = [...partsItems];
                                                updatedParts[index].unitPrice = newVal;
                                                updatedParts[index].rate = newVal; // keep both fields in sync
                                                setPartsItems(updatedParts);
                                            }}
                                            onBlur={(e: any) => {
                                                const newVal = e.target.value;
                                                const updatedParts = [...partsItems];
                                                updatedParts[index].unitPrice = isNaN(newVal) ? 0 : (+newVal > -1 ? +newVal : 0);
                                                updatedParts[index].rate = updatedParts[index].unitPrice;
                                                setPartsItems(updatedParts);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={`w-[120px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col editable cursor-pointer`} id={`part-item-qty-${index}`} onClick={(e: any) => {
                                handleOnClickCellEdit(`part-item-qty-${index}`, e);
                                e.stopPropagation();
                                e.preventDefault();
                            }}>
                                <div className={`flex items-center gap-1 flex-nowrap justify-end`}>
                                    <div className={`w-20 min-w-20 text-right`}>
                                        <div className={`preview-value`} title={(+part.requiredQty > 0) ? (`Quantity is: ` + part.requiredQty) : `Click here to add data`}>{part.requiredQty || 0}</div>
                                        <div className={`edit-value hidden`}>
                                            {[1, 5].includes(+part.unitID) ? (
                                                <input
                                                    type="tel"
                                                    className={`w-full border border-gray-300 rounded px-2 py-1 outline-none text-right`}
                                                    value={part.requiredQty}
                                                    onChange={(e) => {
                                                        const newQty = +e.target.value;
                                                        const updatedParts = [...partsItems];
                                                        updatedParts[index].requiredQty = isNaN(newQty) ? 0 : newQty;
                                                        setPartsItems(updatedParts);
                                                    }}
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    name={`poinventoryrequiredqty${index}`}
                                                    className={`w-full border border-gray-300 rounded px-2 py-1 outline-none text-right input-no-spinner`}
                                                    value={part.requiredQty}
                                                    min={0}
                                                    onInput={(e: any) => {
                                                        const newQty = e.target.value;
                                                        const updatedParts = [...partsItems];
                                                        updatedParts[index].requiredQty = newQty;
                                                        setPartsItems(updatedParts);
                                                    }}
                                                    onBlur={(e: any) => {
                                                        const newQty = e.target.value;
                                                        const updatedParts = [...partsItems];
                                                        updatedParts[index].requiredQty = isNaN(newQty) ? 0 : (+newQty > -1 ? +newQty : 0);
                                                        setPartsItems(updatedParts);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div>{part.unitName || ``}</div>
                                </div>
                            </div>

                            <div className={`w-[120px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} id={`part-item-total-${index}`} onClick={(e: any) => {
                                handleOnClickCellEdit(`part-item-total-${index}`, e);
                                e.stopPropagation();
                                e.preventDefault();
                            }}>
                                <div className={`preview-value line-clamp-1 text-right`} title={`Click here to edit total`}>
                                    <Currency currency={`USD`} value={Number(((part?.unitPrice ?? part?.rate ?? 0) * (Number(part?.requiredQty || 0))).toFixed(2) || 0)} />
                                </div>
                                <div className={`edit-value hidden`}>
                                    <div className={`flex`}>
                                        <span className={`border border-gray-300 bg-gray-50 rounded-tl rounded-bl flex items-center justify-center px-2 py-1 border-r-0`}>$</span>
                                        <input
                                            type="number"
                                            name={`poinventorytotal${index}`}
                                            className={`w-full border border-gray-300 rounded-tr rounded-br px-2 py-1 outline-none text-right input-no-spinner`}
                                            value={Number(((part?.unitPrice ?? part?.rate ?? 0) * (Number(part?.requiredQty || 0))).toFixed(2) || 0)}
                                            min={0}
                                            onInput={(e: any) => {
                                                const newTotal = parseFloat(e.target.value) || 0;
                                                const qty = Number(part?.requiredQty) || 0;
                                                const updatedParts = [...partsItems];
                                                if (qty > 0) {
                                                    const newUnitPrice = Number((newTotal / qty).toFixed(2));
                                                    updatedParts[index].unitPrice = newUnitPrice;
                                                    updatedParts[index].rate = newUnitPrice;
                                                }
                                                setPartsItems(updatedParts);
                                            }}
                                            onBlur={(e: any) => {
                                                const newTotal = parseFloat(e.target.value) || 0;
                                                const qty = Number(part?.requiredQty) || 0;
                                                const updatedParts = [...partsItems];
                                                if (qty > 0) {
                                                    const newUnitPrice = Number((newTotal / qty).toFixed(2));
                                                    updatedParts[index].unitPrice = newUnitPrice;
                                                    updatedParts[index].rate = newUnitPrice;
                                                }
                                                setPartsItems(updatedParts);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={`w-[32px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3`}>
                                <a onClick={() => {
                                    const updatedParts = [...partsItems];
                                    updatedParts.splice(index, 1);
                                    setPartsItems(updatedParts);
                                }}>
                                    <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect y="0.500977" width="15.0008" height="15" rx="7.5" fill="#E6BDCB" />
                                        <path d="M10.8974 10.9032C10.93 10.9357 10.9557 10.9743 10.9733 11.0168C10.9909 11.0593 11 11.1048 11 11.1508C11 11.1968 10.9909 11.2423 10.9733 11.2848C10.9557 11.3273 10.93 11.3659 10.8974 11.3984C10.8649 11.4309 10.8263 11.4567 10.7838 11.4743C10.7413 11.4919 10.6958 11.501 10.6498 11.501C10.6038 11.501 10.5583 11.4919 10.5158 11.4743C10.4733 11.4567 10.4347 11.4309 10.4022 11.3984L7.5 8.49576L4.59779 11.3984C4.53212 11.4641 4.44305 11.501 4.35018 11.501C4.2573 11.501 4.16823 11.4641 4.10256 11.3984C4.03689 11.3327 4 11.2437 4 11.1508C4 11.0579 4.03689 10.9689 4.10256 10.9032L7.00521 8.00098L4.10256 5.09876C4.03689 5.03309 4 4.94402 4 4.85115C4 4.75828 4.03689 4.66921 4.10256 4.60354C4.16823 4.53787 4.2573 4.50098 4.35018 4.50098C4.44305 4.50098 4.53212 4.53787 4.59779 4.60354L7.5 7.50619L10.4022 4.60354C10.4679 4.53787 10.557 4.50098 10.6498 4.50098C10.7427 4.50098 10.8318 4.53787 10.8974 4.60354C10.9631 4.66921 11 4.75828 11 4.85115C11 4.94402 10.9631 5.03309 10.8974 5.09876L7.99478 8.00098L10.8974 10.9032Z" fill="#F31260" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    ))}

                    {partsItems.length !== 0 && (
                        <>
                            <div className={`flex items-center flex-nowrap border-b border-[#043E6133]`} >
                                <div className={`w-[622px] flex-shrink-0 grow font-normal text-[#42484B] text-[15px] px-2.5 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} >
                                    <div className={`preview-value line-clamp-1`} >Sub Total</div>
                                </div>

                                <div className={`w-[160px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col editable cursor-pointer`} >
                                    <div className={`flex items-center gap-1 flex-nowrap justify-end`}>
                                        <div><Currency currency={`USD`} value={Number(getSubTotal())} /></div>
                                    </div>
                                </div>
                                <div className={`w-8 min-w-8 font-normal text-[#42484B] text-[15px] px-2.5 py-3`}></div>
                            </div>
                            <div className={`flex items-center flex-nowrap border-b border-[#043E6133]`} >
                                <div className={`w-[622px] flex-shrink-0 grow font-normal text-[#42484B] text-[15px] px-2.5 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} >
                                    <div className={`preview-value line-clamp-1`} >Tax</div>
                                </div>

                                <div className={`w-[160px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col editable cursor-pointer`} >
                                    <div className={`flex items-center gap-1 flex-nowrap justify-end`}>
                                        <div>
                                            <SelectBox
                                                defaultValue={tax}
                                                value={tax}
                                                onChange={setTax}
                                                placeholder="Choose Tax"
                                                className="!my-2"
                                                options={taxes}
                                                size="sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={`w-8 min-w-8 font-normal text-[#42484B] text-[15px] px-2.5 py-3`}></div>
                            </div>

                            <div className={`flex items-center flex-nowrap`} >
                                <div className={`w-[622px] flex-shrink-0 grow font-normal text-[#42484B] text-[15px] px-2.5 min-h-12 max-h-12 flex justify-center flex-col cursor-pointer editable`} >
                                    <div className={`preview-value line-clamp-1 font-semibold`} >Total</div>
                                </div>

                                <div className={`w-[160px] flex-shrink-0 font-normal text-[#42484B] text-[15px] px-2.5 py-3 min-h-12 max-h-12 flex justify-center flex-col editable cursor-pointer`} >
                                    <div className={`flex items-center gap-1 flex-nowrap justify-end`}>
                                        <div><Currency currency={`USD`} value={Number(getTotal())} /></div>
                                    </div>
                                </div>
                                <div className={`w-8 min-w-8 font-normal text-[#42484B] text-[15px] px-2.5 py-3`}></div>
                            </div>
                        </>
                    )}

                </div>
            </div>

        </div>
    );
};
export default CreateEmergencyPurchaseOrderParts;